import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import axios from "axios";
import { API_URL } from "../../api-config";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Using standard Feather icons or similar if available, or just text/emoji if no other lib. 
// Since MUI icons are also requested to be removed (implied by "no mui"), I will try to use 
// generic HTML/CSS or assume font-awesome/heroicons are not installed and stick to simple text labels or SVGs 
// if I want to be strictly "no extra libs".
// However, typically in these environments, some icon set exists. 
// I'll use simple text/emoji for now to be safe and purely "no mui", or just omit icons to keep it clean standard HTML.
// Actually, I can use an SVG icon set if I inline them, but that makes code long.
// I will stick to clean standard HTML/CSS for now.

const FixClient = () => {
    const [sessions, setSessions] = useState([]);
    const [formData, setFormData] = useState({
        sender_comp_id: "",
        target_comp_id: "",
        username: "",
        password: "",
        ip_whitelist: "",
        is_active: true
    });
    const [loading, setLoading] = useState(true);

    const fetchSessions = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/usdclp-api/price/fix_session`);
            setSessions(response.data);
        } catch (error) {
            console.error("Error fetching sessions:", error);
            toast.error("Error loading sessions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, []);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const cleanedData = {
                ...formData,
                ip_whitelist: formData.ip_whitelist
                    ? formData.ip_whitelist.split(',').map(ip => ip.trim()).filter(ip => ip).join(',')
                    : ""
            };

            await axios.post(`${API_URL}/usdclp-api/price/fix_session`, cleanedData);
            toast.success("Session created successfully");
            fetchSessions();
            setFormData({
                sender_comp_id: "",
                target_comp_id: "",
                username: "",
                password: "",
                ip_whitelist: "",
                is_active: true
            });
        } catch (error) {
            console.error("Error creating session:", error);
            toast.error("Error creating session");
        }
    };

    return (
        <div className="m-5">
            <div className="flex justify-between items-center mb-6">
                <Header title="FIX CLIENTS" subtitle="Manage FIX Protocol Sessions" />
                <button
                    onClick={fetchSessions}
                    className="px-4 py-2 border border-emerald-500 text-emerald-500 hover:bg-emerald-500 hover:text-white rounded transition-colors"
                >
                    Refresh List
                </button>
            </div>
            <ToastContainer position="bottom-right" theme="dark" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* CREATE SESSION FORM */}
                <div className="md:col-span-1">
                    <div className="bg-slate-800 p-6 rounded-lg shadow-lg h-full">
                        <div className="flex items-center gap-2 mb-6 text-gray-100">
                            <h4 className="text-xl font-semibold">New Session</h4>
                        </div>

                        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                            <div>
                                <label className="block text-gray-400 text-sm mb-1">Sender Comp ID</label>
                                <input
                                    type="text"
                                    name="sender_comp_id"
                                    value={formData.sender_comp_id}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full bg-slate-700 text-white px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-1">Target Comp ID</label>
                                <input
                                    type="text"
                                    name="target_comp_id"
                                    value={formData.target_comp_id}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full bg-slate-700 text-white px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>

                            <hr className="border-gray-700 my-2" />

                            <div>
                                <label className="block text-gray-400 text-sm mb-1">Username</label>
                                <input
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    className="w-full bg-slate-700 text-white px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-1">Password</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className="w-full bg-slate-700 text-white px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-400 text-sm mb-1">IP Whitelist (Comma separated)</label>
                                <textarea
                                    name="ip_whitelist"
                                    value={formData.ip_whitelist}
                                    onChange={handleInputChange}
                                    rows="2"
                                    className="w-full bg-slate-700 text-white px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={formData.is_active}
                                        onChange={handleInputChange}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                                    <span className="ml-3 text-sm font-medium text-gray-300">Active Status</span>
                                </label>
                            </div>

                            <button
                                type="submit"
                                className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-4 rounded transition-colors shadow-lg"
                            >
                                Create Session
                            </button>
                        </form>
                    </div>
                </div>

                {/* SESSIONS LIST */}
                <div className="md:col-span-2">
                    <div className="bg-slate-800 rounded-lg shadow-lg overflow-hidden h-[75vh] flex flex-col">
                        <div className="overflow-auto flex-1">
                            <table className="w-full text-left text-sm text-gray-400">
                                <thead className="bg-blue-900 text-gray-100 uppercase sticky top-0">
                                    <tr>
                                        <th className="px-6 py-3">ID</th>
                                        <th className="px-6 py-3">Sender Comp ID</th>
                                        <th className="px-6 py-3">Target Comp ID</th>
                                        <th className="px-6 py-3">Username</th>
                                        <th className="px-6 py-3">IP Whitelist</th>
                                        <th className="px-6 py-3 text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sessions.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center">No sessions found</td>
                                        </tr>
                                    )}
                                    {sessions.map((session) => (
                                        <tr key={session.id} className="border-b border-gray-700 hover:bg-slate-700 transition-colors">
                                            <td className="px-6 py-4 font-medium text-gray-100">#{session.id}</td>
                                            <td className="px-6 py-4 text-emerald-400 font-medium">{session.sender_comp_id}</td>
                                            <td className="px-6 py-4">{session.target_comp_id}</td>
                                            <td className="px-6 py-4">{session.username || "-"}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {session.ip_whitelist ? (
                                                        session.ip_whitelist.split(',').map((ip, i) => (
                                                            <span key={i} className="bg-blue-800 text-blue-100 text-xs font-medium px-2 py-0.5 rounded">
                                                                {ip.trim()}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="text-gray-500 italic">Any</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`px-2 py-1 rounded text-xs font-semibold ${session.is_active ? 'bg-emerald-900 text-emerald-300' : 'bg-red-900 text-red-300'}`}>
                                                    {session.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {loading && (
                                <div className="flex justify-center items-center p-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FixClient;
